import { FC, useState } from 'react';

import './PayTutionModal.scss';
import useAppSelector from 'hooks/common/useAppSelector';

import bspbImage from '../../../../assets/images/bspb.webp';
import gazpromImage from '../../../../assets/images/gazprombank.webp';
import sberImage from '../../../../assets/images/sber.webp';
import TemplateModal from '../TemplateModal';

interface BankInfo {
  id: string;
  name: string;
  image: string;
  href: string;
  alt: string;
}

const PayTuitionModal: FC = () => {
  const [loadedImages, setLoadedImages] = useState<Record<string, boolean>>({});
  const [errorImages, setErrorImages] = useState<Record<string, boolean>>({});
  const [loadingImages, setLoadingImages] = useState<Record<string, boolean>>(
    {}
  );

  const modalData = useAppSelector(state => state.modal?.data);
  const contractNumber = modalData?.contractNumber;

  const getBankHref = (baseHref: string) => {
    if (!contractNumber) return baseHref;
    return baseHref.replace(
      'номер_договора',
      encodeURIComponent(contractNumber)
    );
  };

  const banks: BankInfo[] = [
    {
      id: 'sber',
      name: 'Сбербанк',
      image: sberImage,
      alt: 'Сбербанк',
      href: 'https://e-pay.spbstu.ru/sberbank/contract/номер_договора/',
    },
    {
      id: 'bspb',
      name: 'Банк Санкт-Петербург',
      image: bspbImage,
      alt: 'Банк Санкт-Петербург',
      href: 'https://pay.spbstu.ru/www/form_pay.html?payer_data=номер_договора&payer_base=Оплата%20за%20обучение',
    },
    {
      id: 'gazprom',
      name: 'Газпромбанк',
      image: gazpromImage,
      alt: 'Газпромбанк',
      href: 'https://e-pay.spbstu.ru/gazprombank/contract/номер_договора/',
    },
  ];

  const handleImageLoad = (bankId: string) => {
    setLoadedImages(prev => ({ ...prev, [bankId]: true }));
    setLoadingImages(prev => ({ ...prev, [bankId]: false }));
    setErrorImages(prev => ({ ...prev, [bankId]: false }));
  };

  const handleImageError = (bankId: string) => {
    setErrorImages(prev => ({ ...prev, [bankId]: true }));
    setLoadingImages(prev => ({ ...prev, [bankId]: false }));
    setLoadedImages(prev => ({ ...prev, [bankId]: false }));
  };

  const handleImageStart = (bankId: string) => {
    setLoadingImages(prev => ({ ...prev, [bankId]: true }));
  };

  const retryLoadImage = (bank: BankInfo) => {
    setErrorImages(prev => ({ ...prev, [bank.id]: false }));
    setLoadingImages(prev => ({ ...prev, [bank.id]: true }));

    const img = new Image();
    img.src = bank.image;
    img.onload = () => handleImageLoad(bank.id);
    img.onerror = () => handleImageError(bank.id);
  };

  const getBankClassName = (bankId: string) => {
    let className = 'bank-link';
    if (loadingImages[bankId]) className += ' bank-link--loading';
    if (errorImages[bankId]) className += ' bank-link--error';
    return className;
  };

  return (
    <TemplateModal title="Выберите банк:">
      <div className="pay-tuition-modal">
        {banks.map(bank => (
          <div key={bank.id} style={{ width: '100%' }}>
            {!errorImages[bank.id] ? (
              <a
                href={getBankHref(bank.href)}
                target="_blank"
                rel="noopener noreferrer"
                className={getBankClassName(bank.id)}
                onClick={e => {
                  if (loadingImages[bank.id]) {
                    e.preventDefault();
                  }
                }}
              >
                <img
                  src={bank.image}
                  alt={bank.alt}
                  onLoadStart={() => handleImageStart(bank.id)}
                  onLoad={() => handleImageLoad(bank.id)}
                  onError={() => handleImageError(bank.id)}
                  style={{
                    opacity: loadedImages[bank.id] ? 1 : 0,
                    transition: 'opacity 0.3s ease',
                  }}
                />
                {loadingImages[bank.id] && !loadedImages[bank.id] && (
                  <div
                    style={{
                      position: 'absolute',
                      left: '17px',
                      color: '#666',
                      fontSize: '14px',
                    }}
                  >
                    Загрузка...
                  </div>
                )}
              </a>
            ) : (
              <div className="bank-link bank-link--error">
                <div className="error-content">
                  <span className="error-message">
                    Не удалось загрузить {bank.name}
                  </span>
                  <button
                    className="retry-button"
                    onClick={() => retryLoadImage(bank)}
                  >
                    Попробовать снова
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </TemplateModal>
  );
};

export default PayTuitionModal;
